import React from 'react';
import styled, { css } from 'styled-components';

const ButtonBase = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
  gap: 0.5rem;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  ${props => props.fullWidth && css`
    width: 100%;
  `}
  
  ${props => props.size === 'small' && css`
    padding: 0.3rem 0.8rem;
    font-size: 0.8rem;
  `}
  
  ${props => props.size === 'large' && css`
    padding: 0.7rem 1.5rem;
    font-size: 1rem;
  `}
`;

const PrimaryButton = styled(ButtonBase)`
  background: var(--primary);
  color: white;
  box-shadow: 0 2px 5px rgba(0, 102, 255, 0.2);
  
  &:hover:not(:disabled) {
    background: var(--primary-dark);
    box-shadow: 0 4px 8px rgba(0, 102, 255, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: 0 1px 3px rgba(0, 102, 255, 0.2);
  }
`;

const SecondaryButton = styled(ButtonBase)`
  background: rgba(0, 102, 255, 0.1);
  color: var(--primary);
  
  &:hover:not(:disabled) {
    background: rgba(0, 102, 255, 0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const OutlineButton = styled(ButtonBase)`
  background: transparent;
  color: var(--primary);
  border: 1px solid var(--primary);
  
  &:hover:not(:disabled) {
    background: rgba(0, 102, 255, 0.05);
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const TextButton = styled(ButtonBase)`
  background: transparent;
  color: var(--primary);
  padding: 0.5rem;
  
  &:hover:not(:disabled) {
    background: rgba(0, 102, 255, 0.05);
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const DangerButton = styled(ButtonBase)`
  background: #EF4444;
  color: white;
  
  &:hover:not(:disabled) {
    background: #DC2626;
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'medium', 
  fullWidth = false,
  icon,
  iconPosition = 'left',
  ...props 
}) => {
  const renderContent = () => (
    <>
      {icon && iconPosition === 'left' && <span className="button-icon">{icon}</span>}
      {children}
      {icon && iconPosition === 'right' && <span className="button-icon">{icon}</span>}
    </>
  );

  switch (variant) {
    case 'secondary':
      return (
        <SecondaryButton size={size} fullWidth={fullWidth} {...props}>
          {renderContent()}
        </SecondaryButton>
      );
    case 'outline':
      return (
        <OutlineButton size={size} fullWidth={fullWidth} {...props}>
          {renderContent()}
        </OutlineButton>
      );
    case 'text':
      return (
        <TextButton size={size} fullWidth={fullWidth} {...props}>
          {renderContent()}
        </TextButton>
      );
    case 'danger':
      return (
        <DangerButton size={size} fullWidth={fullWidth} {...props}>
          {renderContent()}
        </DangerButton>
      );
    case 'primary':
    default:
      return (
        <PrimaryButton size={size} fullWidth={fullWidth} {...props}>
          {renderContent()}
        </PrimaryButton>
      );
  }
};

export default Button;
